import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  ToggleButton, ToggleButtonGroup, RadioGroup, FormControlLabel, Radio,
  Alert, CircularProgress, Snackbar, Divider, Tabs, Tab, Paper,
  Grid, Autocomplete,
} from '@mui/material';
import { Add, Send, Cancel, Edit, Delete, Campaign as CampaignIcon } from '@mui/icons-material';
import {
  fetchCampaigns, createCampaign, updateCampaign, deleteCampaign,
  sendCampaign, cancelCampaign, fetchSegments,
} from '../../api/api';

const STATUS_COLORS = {
  draft: 'default',
  scheduled: 'primary',
  sending: 'warning',
  sent: 'success',
  cancelled: 'error',
};

const TYPE_COLORS = {
  push: '#667eea',
  email: '#10b981',
  sms: '#f59e0b',
  'in-app': '#8b5cf6',
};

const EMPTY_FORM = {
  name: '', description: '', type: 'push',
  targetType: 'all', segmentId: '', customUserIds: '',
  title: '', body: '', imageUrl: '', deepLink: '',
  scheduleType: 'now', scheduledAt: '',
};

export default function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [cRes, sRes] = await Promise.allSettled([fetchCampaigns(), fetchSegments()]);
      if (cRes.status === 'fulfilled') setCampaigns(cRes.value.data?.campaigns || cRes.value.data || []);
      if (sRes.status === 'fulfilled') setSegments(sRes.value.data?.segments || sRes.value.data || []);
    } catch (e) {
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const filtered = tab === 'all' ? campaigns : campaigns.filter(c => c.type === tab);

  const openCreate = () => {
    setEditCampaign(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditCampaign(c);
    setForm({
      name: c.name || '',
      description: c.description || '',
      type: c.type || 'push',
      targetType: c.targetAudience?.type || 'all',
      segmentId: c.targetAudience?.segmentId || '',
      customUserIds: (c.targetAudience?.userIds || []).join(', '),
      title: c.content?.title || '',
      body: c.content?.body || '',
      imageUrl: c.content?.imageUrl || '',
      deepLink: c.content?.deepLink || '',
      scheduleType: c.scheduledAt ? 'schedule' : 'now',
      scheduledAt: c.scheduledAt ? c.scheduledAt.slice(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return showSnack('Name is required', 'error');
    if (!form.title.trim()) return showSnack('Content title is required', 'error');
    if (!form.body.trim()) return showSnack('Message body is required', 'error');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        type: form.type,
        targetAudience: {
          type: form.targetType,
          ...(form.targetType === 'segment' && { segmentId: form.segmentId }),
          ...(form.targetType === 'custom' && { userIds: form.customUserIds.split(',').map(s => s.trim()).filter(Boolean) }),
        },
        content: {
          title: form.title,
          body: form.body,
          ...(form.imageUrl && { imageUrl: form.imageUrl }),
          ...(form.deepLink && { deepLink: form.deepLink }),
        },
        ...(form.scheduleType === 'schedule' && form.scheduledAt && { scheduledAt: new Date(form.scheduledAt).toISOString() }),
      };
      if (editCampaign) {
        await updateCampaign(editCampaign._id, payload);
        showSnack('Campaign updated');
      } else {
        await createCampaign(payload);
        showSnack('Campaign created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      showSnack(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id) => {
    setSending(true);
    try {
      const res = await sendCampaign(id);
      setSendConfirm(null);
      const stats = res.data?.stats || res.data;
      showSnack(`Campaign sent! Targeted: ${stats?.targeted || 0}, Sent: ${stats?.sent || 0}`);
      load();
    } catch (e) {
      showSnack(e.response?.data?.message || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelCampaign(id);
      showSnack('Campaign cancelled');
      load();
    } catch (e) {
      showSnack('Cancel failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      setDeleteConfirm(null);
      showSnack('Campaign deleted');
      load();
    } catch (e) {
      showSnack('Delete failed', 'error');
    }
  };

  const TABS = [
    { value: 'all', label: 'All' },
    { value: 'push', label: 'Push' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'in-app', label: 'In-App' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Campaign Manager</Typography>
          <Typography variant="body2" color="text.secondary">Create and manage marketing campaigns</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
          Create Campaign
        </Button>
      </Box>

      <Paper sx={{ bgcolor: 'background.paper', borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
        </Tabs>
      </Paper>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && filtered.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
          <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No campaigns yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Create your first marketing campaign</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Create Campaign
          </Button>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filtered.map(c => (
          <Card key={c._id} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography fontWeight={800} color="text.primary">{c.name}</Typography>
                    <Chip
                      label={c.type?.toUpperCase()}
                      size="small"
                      sx={{ bgcolor: TYPE_COLORS[c.type] || '#667eea', color: '#fff', fontWeight: 700, fontSize: 10 }}
                    />
                    <Chip
                      label={c.status || 'draft'}
                      size="small"
                      color={STATUS_COLORS[c.status] || 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  {c.description && <Typography variant="body2" color="text.secondary">{c.description}</Typography>}
                  <Typography variant="caption" color="text.secondary">
                    Target: {c.targetAudience?.type === 'all' ? 'All Users' : c.targetAudience?.type === 'segment' ? `Segment: ${c.targetAudience?.segmentId}` : 'Custom'}
                  </Typography>
                  {c.scheduledAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Scheduled: {new Date(c.scheduledAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                {c.status === 'sent' && c.stats && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Targeted', value: c.stats.targeted },
                      { label: 'Sent', value: c.stats.sent },
                      { label: 'Failed', value: c.stats.failed },
                      { label: 'Opened', value: c.stats.opened },
                    ].map(s => (
                      <Box key={s.label} sx={{ textAlign: 'center' }}>
                        <Typography fontWeight={800} color="text.primary">{s.value ?? 0}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
              {(c.status === 'draft' || !c.status) && (
                <>
                  <Button size="small" startIcon={<Edit />} onClick={() => openEdit(c)}>Edit</Button>
                  <Button size="small" variant="contained" color="success" startIcon={<Send />}
                    onClick={() => setSendConfirm(c)}>
                    Send Now
                  </Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => setDeleteConfirm(c)}>Delete</Button>
                </>
              )}
              {c.status === 'scheduled' && (
                <Button size="small" color="warning" startIcon={<Cancel />} onClick={() => handleCancel(c._id)}>
                  Cancel Schedule
                </Button>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{editCampaign ? 'Edit Campaign' : 'Create Campaign'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField label="Campaign Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Type</Typography>
              <ToggleButtonGroup value={form.type} exclusive onChange={(_, v) => v && setForm(f => ({ ...f, type: v }))} size="small">
                {['push', 'in-app', 'email', 'sms'].map(t => (
                  <ToggleButton key={t} value={t} sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{t}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Target Audience</Typography>
              <RadioGroup row value={form.targetType} onChange={e => setForm(f => ({ ...f, targetType: e.target.value }))}>
                <FormControlLabel value="all" control={<Radio />} label="All Users" />
                <FormControlLabel value="segment" control={<Radio />} label="Customer Segment" />
                <FormControlLabel value="custom" control={<Radio />} label="Custom" />
              </RadioGroup>
              {form.targetType === 'segment' && (
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Select Segment</InputLabel>
                  <Select value={form.segmentId} label="Select Segment" onChange={e => setForm(f => ({ ...f, segmentId: e.target.value }))}>
                    {segments.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {form.targetType === 'custom' && (
                <TextField
                  label="User IDs (comma separated)"
                  value={form.customUserIds}
                  onChange={e => setForm(f => ({ ...f, customUserIds: e.target.value }))}
                  multiline rows={2} fullWidth sx={{ mt: 1 }}
                  placeholder="user1_id, user2_id, ..."
                />
              )}
            </Box>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>Content</Typography>
            <TextField label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Message Body *" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              multiline rows={3} fullWidth />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Image URL (optional)" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Deep Link (optional)" value={form.deepLink} onChange={e => setForm(f => ({ ...f, deepLink: e.target.value }))} fullWidth />
              </Grid>
            </Grid>

            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Schedule</Typography>
              <RadioGroup row value={form.scheduleType} onChange={e => setForm(f => ({ ...f, scheduleType: e.target.value }))}>
                <FormControlLabel value="now" control={<Radio />} label="Send Now" />
                <FormControlLabel value="schedule" control={<Radio />} label="Schedule" />
              </RadioGroup>
              {form.scheduleType === 'schedule' && (
                <TextField label="Scheduled At" type="datetime-local" value={form.scheduledAt}
                  onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                  InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} fullWidth />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : (editCampaign ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Confirm */}
      <Dialog open={Boolean(sendConfirm)} onClose={() => setSendConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>Send Campaign</DialogTitle>
        <DialogContent>
          <Typography>Send <strong>{sendConfirm?.name}</strong> immediately to all targeted users?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSendConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => handleSend(sendConfirm?._id)} disabled={sending}>
            {sending ? <CircularProgress size={20} /> : 'Send Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?._id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
